import React, { useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import {
  ClockCircleOutlined,
  DatabaseOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Descriptions, Popover, Space, Tag } from 'antd';

import { ConfirmModal } from '@/shared/ui/ConfirmModal';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';

import { useClearLogs, usePerformanceLogs } from '@/entities/performance';
import type { PerformanceLogFilter } from '@/entities/performance';

import { PerformanceLogList } from '@/widgets/performance/PerformanceLogList';
import { PerformanceStats } from '@/widgets/performance/PerformanceStats';

import styles from './PerformanceMonitoringPage.module.css';

export const PerformanceMonitoringPage: React.FC = () => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const filter: PerformanceLogFilter = {
    type: 'all',
    showErrorsOnly: false,
  };

  const { logs, stats } = usePerformanceLogs(filter);
  const { clearLogs } = useClearLogs();

  const handleSelectType = (type: string) => {
    setSelectedType((prev) => (prev === type ? null : type));
  };

  const handleClearLogs = () => {
    setClearConfirmOpen(true);
  };

  const handleClearExecute = () => {
    clearLogs();
  };

  const infoContent = (
    <div className={styles.infoContent}>
      <Descriptions title="수집 대상" column={1} bordered size="small" className={styles.descriptions}>
        <Descriptions.Item
          label={
            <Space>
              <WarningOutlined style={{ color: '#ff4d4f' }} />
              <span>에러</span>
            </Space>
          }>
          <Space size={[4, 4]} wrap>
            <Tag color="red">API 에러</Tag>
            <Tag color="purple">타임아웃</Tag>
            <Tag color="orange">Connection Reset</Tag>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <Space>
              <ClockCircleOutlined style={{ color: '#faad14' }} />
              <span>느린 쿼리</span>
            </Space>
          }>
          <span>응답시간 3초 이상</span>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <Space>
              <ThunderboltOutlined style={{ color: '#52c41a' }} />
              <span>성공 쿼리</span>
            </Space>
          }>
          <span>통계 계산용 (메모리에만 저장, 최대 100건)</span>
        </Descriptions.Item>
      </Descriptions>

      <Descriptions title="저장 정책" column={1} bordered size="small" className={styles.descriptions}>
        <Descriptions.Item
          label={
            <Space>
              <SaveOutlined style={{ color: '#1890ff' }} />
              <span>localStorage</span>
            </Space>
          }>
          <div>에러와 느린 쿼리 최대 100건 저장</div>
          <div className={styles.subText}>24시간 후 자동 삭제</div>
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <Space>
              <DatabaseOutlined style={{ color: '#13c2c2' }} />
              <span>메모리</span>
            </Space>
          }>
          <div>성공 쿼리 최대 100건 저장</div>
          <div className={styles.subText}>응답시간 통계 계산에만 사용</div>
        </Descriptions.Item>
      </Descriptions>

      <Descriptions title="상세 정보" column={1} bordered size="small" className={styles.descriptions}>
        <Descriptions.Item
          label={
            <Space>
              <InfoCircleOutlined style={{ color: '#722ed1' }} />
              <span>테이블</span>
            </Space>
          }>
          행 클릭 시 에러 응답, 스택 트레이스 등 확인 가능
        </Descriptions.Item>
      </Descriptions>
    </div>
  );

  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <ErrorFallback
          {...props}
          title="성능 모니터링 오류"
          description="성능 모니터링 데이터를 불러오는 중 오류가 발생했습니다."
        />
      )}>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <div className={styles.pageTitle}>성능 모니터링</div>
            <Popover content={infoContent} trigger="click" placement="bottomLeft" arrow={false}>
              <InfoCircleOutlined className={styles.infoIcon} />
            </Popover>
          </div>
        </div>

        <div className={styles.statsSection}>
          <PerformanceStats stats={stats} selectedType={selectedType} onSelectType={handleSelectType} />
        </div>

        <div className={styles.tableSection}>
          <PerformanceLogList
            logs={logs}
            onClear={handleClearLogs}
            selectedType={selectedType}
            onFilterChange={setSelectedType}
          />
        </div>
      </div>

      <ConfirmModal
        open={clearConfirmOpen}
        message={
          <>
            모든 성능 로그를 삭제하시겠습니까?
            <br />이 작업은 되돌릴 수 없습니다.
          </>
        }
        confirmText="삭제"
        cancelText="취소"
        confirmButtonProps={{ danger: true }}
        onConfirm={handleClearExecute}
        onCancel={() => setClearConfirmOpen(false)}
      />
    </ErrorBoundary>
  );
};
